import { useState, ChangeEvent } from "react";
import { InformationRequest } from "./CaseDetailDrawer";
import { Alert, Button, Dialog, TextInput, Select, Row } from "@narmi/design_system";
import { Case } from "./CaseTable";
import { INFORMATION_REQUEST_RESOLUTIONS, INFORMATION_REQUEST_RESOLUTIONS_TYPE } from "./codesets";
import { dateOptions } from "./PaymentDetailDrawer";
import { CSRF_TOKEN } from "@pages/csrf";

export default function InformationRequestResponseDialog({
  action,
  setAction,
  caseobj,
  informationRequests,
  successCallback,
}: {
  action: "respond" | "close" | null;
  setAction: CallableFunction;
  caseobj: Case;
  informationRequests: Map<string, InformationRequest>;
  successCallback: CallableFunction;
}) {
  const [error, setError]: [string | null, CallableFunction] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [status, setStatus] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");

  async function closeInfoReq() {
    setDisableSubmit(true);
    const path = `/v1/fednow/cases/${caseobj?.case_identification}/manually_close`;
    const res = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
    });
    if (!res.ok) {
      const resData = await res.json();
      if (typeof resData.message === "string") {
        setError(resData.message);
      } else {
        setError(Object.values(resData)[0]);
      }
      setDisableSubmit(false);
      return;
    }
    successCallback();
    setDisableSubmit(false);
    setAction(null);
  }

  async function respondToInfoReq() {
    setDisableSubmit(true);
    const path = `/v1/fednow/cases/${caseobj?.case_identification}/information_request_response`;
    const res = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
      body: JSON.stringify({
        status: status,
        additional_info: additionalInfo,
        information_request_ids: Array.from(informationRequests.keys()),
      }),
    });
    if (!res.ok) {
      const resData = await res.json();
      if (typeof resData.message === "string") {
        setError(resData.message);
      } else {
        setError(Object.values(resData)[0]);
      }
      setDisableSubmit(false);
      return;
    }
    successCallback();
    setDisableSubmit(false);
    setAction(null);
  }

  if (action === null) {
    return null;
  }

  const title = action === "close" ? "Manually close information request" : "Respond to request";
  const informationRequest = Array.from(informationRequests.values())[0];
  let description = "";
  if (informationRequest?.aml) {
    description = "Anti-money laundering request";
  } else if (informationRequest?.kind === "duplicate") {
    description = "Possible duplicate payment";
  } else {
    description = informationRequest?.narrative || "";
  }

  return (
    <Dialog isOpen={action !== null} title={title} onUserDismiss={() => setAction(null)}>
      {error && (
        <div>
          <Alert isActive kind="error">
            {error}
          </Alert>
        </div>
      )}

      {action === "close" && (
        <p className="padding--bottom--s">
          Are you sure you want to close this case? Once closed, subsequent responses will not
          re-open it.
        </p>
      )}

      {action === "respond" && (
        <div className="padding--bottom--s">
          <small
            className="padding--bottom"
            style={{ textTransform: "uppercase", fontWeight: "bold" }}
          >
            {new Date(informationRequest.created_at).toLocaleString("en-US", dateOptions)}
          </small>
          <p>{description}</p>
        </div>
      )}

      {action === "respond" && (
        <div className="padding--bottom--s">
          <Select
            label="Resolution"
            onChange={(val: string) => {
              setStatus(val);
            }}
          >
            {Object.keys(INFORMATION_REQUEST_RESOLUTIONS).map((r) => {
              return (
                <Select.Item value={r}>
                  {INFORMATION_REQUEST_RESOLUTIONS[r as INFORMATION_REQUEST_RESOLUTIONS_TYPE]}
                </Select.Item>
              );
            })}
          </Select>
        </div>
      )}

      {action === "respond" && (
        <div className="padding--bottom--s">
          <TextInput
            label="Additional information"
            error={
              status === "INFO" && additionalInfo === ""
                ? "Additional information is required."
                : null
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setAdditionalInfo(e.target.value);
            }}
          />
        </div>
      )}

      <Row justifyContent="end" alignItems="center">
        <Row.Item shrink>
          <Button
            label="Submit"
            onClick={action === "close" ? closeInfoReq : respondToInfoReq}
            disabled={disableSubmit || (action === "respond" && status === "")}
          />
        </Row.Item>
      </Row>
    </Dialog>
  );
}
